import * as T from './ProductCatalogChatbotConstants';
import type {
    PCChatbotIdentity,
    PCConversation,
    PCConversationSummary,
    PCMessage,
    PCActionCard,
    PCChannel,
    PCSyncCard,
    PCSyncResultRow,
    PCLogEntry,
    PCDuration,
    PCUndoResponse,
    PCCta,
} from '../../../interface/ProductCatalogChatbotInterface';

/**
 * Every `*_REQUEST` action carries the caller `identity` (§1) so the saga can
 * attach the mandatory headers. The component builds `identity` from the selected
 * location + logged-in user and passes it on dispatch.
 */

// ── Create / resume conversation (§4.1) ─────────────────────────────────────────

export const createConversationRequest = (payload: {
    identity: PCChatbotIdentity;
    fresh?: boolean;
    title?: string;
}) => ({ type: T.CREATE_CONVERSATION_REQUEST as typeof T.CREATE_CONVERSATION_REQUEST, payload });

export const createConversationSuccess = (data: PCConversation) => ({
    type: T.CREATE_CONVERSATION_SUCCESS as typeof T.CREATE_CONVERSATION_SUCCESS,
    payload: data,
});

export const createConversationFailure = (error: string) => ({
    type: T.CREATE_CONVERSATION_FAILURE as typeof T.CREATE_CONVERSATION_FAILURE,
    payload: { error },
});

// ── List conversations / sidebar (§4.2) ─────────────────────────────────────────

export const listConversationsRequest = (payload: { identity: PCChatbotIdentity }) => ({
    type: T.LIST_CONVERSATIONS_REQUEST as typeof T.LIST_CONVERSATIONS_REQUEST,
    payload,
});

export const listConversationsSuccess = (conversations: PCConversationSummary[]) => ({
    type: T.LIST_CONVERSATIONS_SUCCESS as typeof T.LIST_CONVERSATIONS_SUCCESS,
    payload: conversations,
});

export const listConversationsFailure = (error: string) => ({
    type: T.LIST_CONVERSATIONS_FAILURE as typeof T.LIST_CONVERSATIONS_FAILURE,
    payload: { error },
});

// ── Load one thread (§4.3) ──────────────────────────────────────────────────────

export const loadConversationRequest = (payload: { identity: PCChatbotIdentity; conversationId: string }) => ({
    type: T.LOAD_CONVERSATION_REQUEST as typeof T.LOAD_CONVERSATION_REQUEST,
    payload,
});

export const loadConversationSuccess = (data: PCConversation) => ({
    type: T.LOAD_CONVERSATION_SUCCESS as typeof T.LOAD_CONVERSATION_SUCCESS,
    payload: data,
});

export const loadConversationFailure = (error: string) => ({
    type: T.LOAD_CONVERSATION_FAILURE as typeof T.LOAD_CONVERSATION_FAILURE,
    payload: { error },
});

// ── Send a message (§4.4) ───────────────────────────────────────────────────────

export const sendMessageRequest = (payload: {
    identity: PCChatbotIdentity;
    conversationId: string;
    text: string;
}) => ({ type: T.SEND_MESSAGE_REQUEST as typeof T.SEND_MESSAGE_REQUEST, payload });

export const sendMessageSuccess = (payload: { conversationId: string; messages: PCMessage[] }) => ({
    type: T.SEND_MESSAGE_SUCCESS as typeof T.SEND_MESSAGE_SUCCESS,
    payload,
});

export const sendMessageFailure = (error: string) => ({
    type: T.SEND_MESSAGE_FAILURE as typeof T.SEND_MESSAGE_FAILURE,
    payload: { error },
});

// ── Rename / delete (§4.5 / §4.6) ───────────────────────────────────────────────

export const renameConversationRequest = (payload: {
    identity: PCChatbotIdentity;
    conversationId: string;
    title: string;
}) => ({ type: T.RENAME_CONVERSATION_REQUEST as typeof T.RENAME_CONVERSATION_REQUEST, payload });

export const renameConversationSuccess = (payload: { conversationId: string; title: string }) => ({
    type: T.RENAME_CONVERSATION_SUCCESS as typeof T.RENAME_CONVERSATION_SUCCESS,
    payload,
});

export const renameConversationFailure = (error: string) => ({
    type: T.RENAME_CONVERSATION_FAILURE as typeof T.RENAME_CONVERSATION_FAILURE,
    payload: { error },
});

export const deleteConversationRequest = (payload: { identity: PCChatbotIdentity; conversationId: string }) => ({
    type: T.DELETE_CONVERSATION_REQUEST as typeof T.DELETE_CONVERSATION_REQUEST,
    payload,
});

export const deleteConversationSuccess = (payload: { conversationId: string }) => ({
    type: T.DELETE_CONVERSATION_SUCCESS as typeof T.DELETE_CONVERSATION_SUCCESS,
    payload,
});

export const deleteConversationFailure = (error: string) => ({
    type: T.DELETE_CONVERSATION_FAILURE as typeof T.DELETE_CONVERSATION_FAILURE,
    payload: { error },
});

// ── Action card ops (§4.7 / §6) ─────────────────────────────────────────────────

export const confirmCardRequest = (payload: { identity: PCChatbotIdentity; cardId: string }) => ({
    type: T.CONFIRM_CARD_REQUEST as typeof T.CONFIRM_CARD_REQUEST,
    payload,
});

export const confirmCardSuccess = (payload: { conversationId: string; messages: PCMessage[] }) => ({
    type: T.CONFIRM_CARD_SUCCESS as typeof T.CONFIRM_CARD_SUCCESS,
    payload,
});

export const confirmCardFailure = (error: string) => ({
    type: T.CONFIRM_CARD_FAILURE as typeof T.CONFIRM_CARD_FAILURE,
    payload: { error },
});

export const cancelCardRequest = (payload: { identity: PCChatbotIdentity; cardId: string }) => ({
    type: T.CANCEL_CARD_REQUEST as typeof T.CANCEL_CARD_REQUEST,
    payload,
});

export const cancelCardSuccess = (card: PCActionCard) => ({
    type: T.CANCEL_CARD_SUCCESS as typeof T.CANCEL_CARD_SUCCESS,
    payload: card,
});

export const cancelCardFailure = (error: string) => ({
    type: T.CANCEL_CARD_FAILURE as typeof T.CANCEL_CARD_FAILURE,
    payload: { error },
});

export const pickCandidateRequest = (payload: {
    identity: PCChatbotIdentity;
    cardId: string;
    candidateId: string;
}) => ({ type: T.PICK_CANDIDATE_REQUEST as typeof T.PICK_CANDIDATE_REQUEST, payload });

// pick-candidate now APPENDS messages (retired picker + fresh confirm card + any next queued
// picker), like confirm — the backend no longer returns a single { card } to replace in place.
export const pickCandidateSuccess = (payload: { conversationId: string; messages: PCMessage[] }) => ({
    type: T.PICK_CANDIDATE_SUCCESS as typeof T.PICK_CANDIDATE_SUCCESS,
    payload,
});

export const pickCandidateFailure = (error: string) => ({
    type: T.PICK_CANDIDATE_FAILURE as typeof T.PICK_CANDIDATE_FAILURE,
    payload: { error },
});

export const setCardChannelsRequest = (payload: {
    identity: PCChatbotIdentity;
    cardId: string;
    rowId: string;
    channels: string[];
}) => ({ type: T.SET_CARD_CHANNELS_REQUEST as typeof T.SET_CARD_CHANNELS_REQUEST, payload });

export const setCardChannelsSuccess = (card: PCActionCard) => ({
    type: T.SET_CARD_CHANNELS_SUCCESS as typeof T.SET_CARD_CHANNELS_SUCCESS,
    payload: card,
});

export const setCardChannelsFailure = (error: string) => ({
    type: T.SET_CARD_CHANNELS_FAILURE as typeof T.SET_CARD_CHANNELS_FAILURE,
    payload: { error },
});

export const setCardDurationRequest = (payload: {
    identity: PCChatbotIdentity;
    cardId: string;
    rowId: string;
    duration: PCDuration;
}) => ({ type: T.SET_CARD_DURATION_REQUEST as typeof T.SET_CARD_DURATION_REQUEST, payload });

export const setCardDurationSuccess = (card: PCActionCard) => ({
    type: T.SET_CARD_DURATION_SUCCESS as typeof T.SET_CARD_DURATION_SUCCESS,
    payload: card,
});

export const setCardDurationFailure = (error: string) => ({
    type: T.SET_CARD_DURATION_FAILURE as typeof T.SET_CARD_DURATION_FAILURE,
    payload: { error },
});

export const removeCardRowRequest = (payload: {
    identity: PCChatbotIdentity;
    cardId: string;
    rowId: string;
}) => ({ type: T.REMOVE_CARD_ROW_REQUEST as typeof T.REMOVE_CARD_ROW_REQUEST, payload });

export const removeCardRowSuccess = (card: PCActionCard) => ({
    type: T.REMOVE_CARD_ROW_SUCCESS as typeof T.REMOVE_CARD_ROW_SUCCESS,
    payload: card,
});

export const removeCardRowFailure = (error: string) => ({
    type: T.REMOVE_CARD_ROW_FAILURE as typeof T.REMOVE_CARD_ROW_FAILURE,
    payload: { error },
});

// ── Channels (§4.8) ─────────────────────────────────────────────────────────────

export const fetchChannelsRequest = (payload: { identity: PCChatbotIdentity }) => ({
    type: T.FETCH_CHANNELS_REQUEST as typeof T.FETCH_CHANNELS_REQUEST,
    payload,
});

export const fetchChannelsSuccess = (channels: PCChannel[]) => ({
    type: T.FETCH_CHANNELS_SUCCESS as typeof T.FETCH_CHANNELS_SUCCESS,
    payload: channels,
});

export const fetchChannelsFailure = (error: string) => ({
    type: T.FETCH_CHANNELS_FAILURE as typeof T.FETCH_CHANNELS_FAILURE,
    payload: { error },
});

// ── Sync (§4.9 / §7) ────────────────────────────────────────────────────────────

export const runSyncRequest = (payload: {
    identity: PCChatbotIdentity;
    conversationId: string;
    msgId: string;
}) => ({ type: T.RUN_SYNC_REQUEST as typeof T.RUN_SYNC_REQUEST, payload });

export const runSyncSuccess = (payload: { msgId: string; card: PCSyncCard }) => ({
    type: T.RUN_SYNC_SUCCESS as typeof T.RUN_SYNC_SUCCESS,
    payload,
});

export const runSyncFailure = (error: string) => ({
    type: T.RUN_SYNC_FAILURE as typeof T.RUN_SYNC_FAILURE,
    payload: { error },
});

export const syncResultsRequest = (payload: {
    identity: PCChatbotIdentity;
    conversationId: string;
    msgId: string;
}) => ({ type: T.SYNC_RESULTS_REQUEST as typeof T.SYNC_RESULTS_REQUEST, payload });

export const syncResultsSuccess = (payload: { msgId: string; card: PCSyncCard }) => ({
    type: T.SYNC_RESULTS_SUCCESS as typeof T.SYNC_RESULTS_SUCCESS,
    payload,
});

export const syncResultsFailure = (error: string) => ({
    type: T.SYNC_RESULTS_FAILURE as typeof T.SYNC_RESULTS_FAILURE,
    payload: { error },
});

export const syncRetryRequest = (payload: {
    identity: PCChatbotIdentity;
    conversationId: string;
    msgId: string;
    previous: PCSyncResultRow[];
}) => ({ type: T.SYNC_RETRY_REQUEST as typeof T.SYNC_RETRY_REQUEST, payload });

export const syncRetrySuccess = (payload: { msgId: string; card: PCSyncCard }) => ({
    type: T.SYNC_RETRY_SUCCESS as typeof T.SYNC_RETRY_SUCCESS,
    payload,
});

export const syncRetryFailure = (error: string) => ({
    type: T.SYNC_RETRY_FAILURE as typeof T.SYNC_RETRY_FAILURE,
    payload: { error },
});

// ── Activity log & undo (§4.10 / §8) ────────────────────────────────────────────

export const fetchLogRequest = (payload: { identity: PCChatbotIdentity }) => ({
    type: T.FETCH_LOG_REQUEST as typeof T.FETCH_LOG_REQUEST,
    payload,
});

export const fetchLogSuccess = (entries: PCLogEntry[]) => ({
    type: T.FETCH_LOG_SUCCESS as typeof T.FETCH_LOG_SUCCESS,
    payload: entries,
});

export const fetchLogFailure = (error: string) => ({
    type: T.FETCH_LOG_FAILURE as typeof T.FETCH_LOG_FAILURE,
    payload: { error },
});

/** `conversationId` is optional here: the saga fills in the latest thread from the store when
 *  the caller doesn't pass one, so the panel doesn't have to know about thread selection. */
export const undoLogEntryRequest = (payload: { identity: PCChatbotIdentity; entryId: string; conversationId?: string | null }) => ({
    type: T.UNDO_LOG_ENTRY_REQUEST as typeof T.UNDO_LOG_ENTRY_REQUEST,
    payload,
});

/** `conversationId` is the thread the request was sent for — kept as the routing fallback for
 *  when the response omits its own echo of it. */
export const undoLogEntrySuccess = (payload: { entryId: string; result: PCUndoResponse; conversationId?: string | null }) => ({
    type: T.UNDO_LOG_ENTRY_SUCCESS as typeof T.UNDO_LOG_ENTRY_SUCCESS,
    payload,
});

export const undoLogEntryFailure = (error: string) => ({
    type: T.UNDO_LOG_ENTRY_FAILURE as typeof T.UNDO_LOG_ENTRY_FAILURE,
    payload: { error },
});

// ── Manual catalogue refresh (§4.11) ────────────────────────────────────────────

export const refreshCatalogueRequest = (payload: { identity: PCChatbotIdentity }) => ({
    type: T.REFRESH_CATALOGUE_REQUEST as typeof T.REFRESH_CATALOGUE_REQUEST,
    payload,
});

export const refreshCatalogueSuccess = (payload: { refreshedAt: string }) => ({
    type: T.REFRESH_CATALOGUE_SUCCESS as typeof T.REFRESH_CATALOGUE_SUCCESS,
    payload,
});

export const refreshCatalogueFailure = (error: string) => ({
    type: T.REFRESH_CATALOGUE_FAILURE as typeof T.REFRESH_CATALOGUE_FAILURE,
    payload: { error },
});

// ── Quick-action CTAs (§Item 6) ─────────────────────────────────────────────────

export const fetchCtasRequest = (payload: { identity: PCChatbotIdentity }) => ({
    type: T.FETCH_CTAS_REQUEST as typeof T.FETCH_CTAS_REQUEST,
    payload,
});

export const fetchCtasSuccess = (ctas: PCCta[]) => ({
    type: T.FETCH_CTAS_SUCCESS as typeof T.FETCH_CTAS_SUCCESS,
    payload: ctas,
});

export const fetchCtasFailure = (error: string) => ({
    type: T.FETCH_CTAS_FAILURE as typeof T.FETCH_CTAS_FAILURE,
    payload: { error },
});

// ── Local view-only mutations ───────────────────────────────────────────────────

export const setActiveConversation = (conversationId: string | null) => ({
    type: T.CHATBOT_SET_ACTIVE_CONVERSATION as typeof T.CHATBOT_SET_ACTIVE_CONVERSATION,
    payload: conversationId,
});

/** Optimistically append a locally-created message (e.g. the user's own bubble). */
export const appendLocalMessage = (message: PCMessage) => ({
    type: T.CHATBOT_APPEND_LOCAL_MESSAGE as typeof T.CHATBOT_APPEND_LOCAL_MESSAGE,
    payload: message,
});

export const resetChatbot = () => ({
    type: T.CHATBOT_RESET as typeof T.CHATBOT_RESET,
});

/** Set the staged "thinking" status text shown while a send/confirm/pick is in flight
 *  (or `null` to clear it). Driven by the saga's `race()` against the real request. */
export const setThinkingStatus = (status: string | null) => ({
    type: T.CHATBOT_THINKING_STATUS as typeof T.CHATBOT_THINKING_STATUS,
    payload: status,
});

/** One second of the sync countdown — patches the sync card's `summary` text by message id. */
export const syncTick = (payload: { msgId: string; remaining: number }) => ({
    type: T.CHATBOT_SYNC_TICK as typeof T.CHATBOT_SYNC_TICK,
    payload,
});

/** Apply an instant local mutation to a card (before the network round-trip settles), or roll
 *  one back on failure by re-applying the captured previous snapshot as the patch. */
export const optimisticCardPatch = (payload: { cardId: string; patch: PCActionCard | Partial<PCActionCard> }) => ({
    type: T.CHATBOT_OPTIMISTIC_CARD_PATCH as typeof T.CHATBOT_OPTIMISTIC_CARD_PATCH,
    payload,
});

/** Predict a fresh thread's title from its first message, instantly — the real server title
 *  lands on the next full conversation reload. */
export const predictTitle = (title: string) => ({
    type: T.CHATBOT_PREDICT_TITLE as typeof T.CHATBOT_PREDICT_TITLE,
    payload: title,
});

/** Dismiss the undo confirmation message (activity log panel only). */
export const clearUndoResult = () => ({
    type: T.CHATBOT_CLEAR_UNDO_RESULT as typeof T.CHATBOT_CLEAR_UNDO_RESULT,
});

/** Consume the post-undo routing signal once the shell has switched to the chat view. */
export const clearUndoNav = () => ({
    type: T.CHATBOT_CLEAR_UNDO_NAV as typeof T.CHATBOT_CLEAR_UNDO_NAV,
});

// ── Action union ────────────────────────────────────────────────────────────────

export type ProductCatalogChatbotAction =
    | ReturnType<typeof createConversationRequest>
    | ReturnType<typeof createConversationSuccess>
    | ReturnType<typeof createConversationFailure>
    | ReturnType<typeof listConversationsRequest>
    | ReturnType<typeof listConversationsSuccess>
    | ReturnType<typeof listConversationsFailure>
    | ReturnType<typeof loadConversationRequest>
    | ReturnType<typeof loadConversationSuccess>
    | ReturnType<typeof loadConversationFailure>
    | ReturnType<typeof sendMessageRequest>
    | ReturnType<typeof sendMessageSuccess>
    | ReturnType<typeof sendMessageFailure>
    | ReturnType<typeof renameConversationRequest>
    | ReturnType<typeof renameConversationSuccess>
    | ReturnType<typeof renameConversationFailure>
    | ReturnType<typeof deleteConversationRequest>
    | ReturnType<typeof deleteConversationSuccess>
    | ReturnType<typeof deleteConversationFailure>
    | ReturnType<typeof confirmCardRequest>
    | ReturnType<typeof confirmCardSuccess>
    | ReturnType<typeof confirmCardFailure>
    | ReturnType<typeof cancelCardRequest>
    | ReturnType<typeof cancelCardSuccess>
    | ReturnType<typeof cancelCardFailure>
    | ReturnType<typeof pickCandidateRequest>
    | ReturnType<typeof pickCandidateSuccess>
    | ReturnType<typeof pickCandidateFailure>
    | ReturnType<typeof setCardChannelsRequest>
    | ReturnType<typeof setCardChannelsSuccess>
    | ReturnType<typeof setCardChannelsFailure>
    | ReturnType<typeof setCardDurationRequest>
    | ReturnType<typeof setCardDurationSuccess>
    | ReturnType<typeof setCardDurationFailure>
    | ReturnType<typeof removeCardRowRequest>
    | ReturnType<typeof removeCardRowSuccess>
    | ReturnType<typeof removeCardRowFailure>
    | ReturnType<typeof fetchChannelsRequest>
    | ReturnType<typeof fetchChannelsSuccess>
    | ReturnType<typeof fetchChannelsFailure>
    | ReturnType<typeof runSyncRequest>
    | ReturnType<typeof runSyncSuccess>
    | ReturnType<typeof runSyncFailure>
    | ReturnType<typeof syncResultsRequest>
    | ReturnType<typeof syncResultsSuccess>
    | ReturnType<typeof syncResultsFailure>
    | ReturnType<typeof syncRetryRequest>
    | ReturnType<typeof syncRetrySuccess>
    | ReturnType<typeof syncRetryFailure>
    | ReturnType<typeof fetchLogRequest>
    | ReturnType<typeof fetchLogSuccess>
    | ReturnType<typeof fetchLogFailure>
    | ReturnType<typeof undoLogEntryRequest>
    | ReturnType<typeof undoLogEntrySuccess>
    | ReturnType<typeof undoLogEntryFailure>
    | ReturnType<typeof refreshCatalogueRequest>
    | ReturnType<typeof refreshCatalogueSuccess>
    | ReturnType<typeof refreshCatalogueFailure>
    | ReturnType<typeof fetchCtasRequest>
    | ReturnType<typeof fetchCtasSuccess>
    | ReturnType<typeof fetchCtasFailure>
    | ReturnType<typeof setActiveConversation>
    | ReturnType<typeof appendLocalMessage>
    | ReturnType<typeof resetChatbot>
    | ReturnType<typeof setThinkingStatus>
    | ReturnType<typeof syncTick>
    | ReturnType<typeof optimisticCardPatch>
    | ReturnType<typeof predictTitle>
    | ReturnType<typeof clearUndoResult>
    | ReturnType<typeof clearUndoNav>;
